import { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
    RiBankCardLine,
    RiArrowDownSLine,
    RiCheckLine,
    RiInformationLine,
    RiCloseCircleLine,
} from "react-icons/ri";
import searchIcon from "../../../../assets/admin/search.svg";

const ProposalOrderPaymentMethod = ({
    payment_conditions_code = "",
    onChange,
    errors = {},
    validationTrigger = 0,
}) => {
    const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
    const token = localStorage.getItem("token");

    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const dropdownRef = useRef(null);

    // Keep errors ref updated so the effect always reads fresh errors without re-triggering on typing
    const errorsRef = useRef(errors);
    errorsRef.current = errors;

    // Auto-scroll to payment condition field when validation fails
    useEffect(() => {
        if (!validationTrigger) return;
        if (errorsRef.current?.payment_conditions_code && dropdownRef.current) {
            dropdownRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [validationTrigger]);

    // 1. Simple API Call with Loading state
    useEffect(() => {
        const fetchPaymentMethods = async () => {
            setIsLoading(true);
            try {
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                const response = await axios.get(`${apiUrl}/api/v1/payment-methods`, {
                    headers,
                });

                if (response?.status === 200) {
                    setPaymentMethods(response?.data?.data || []);
                }
            } catch (error) {
                console.error("Error fetching payment methods:", error);
                setPaymentMethods([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPaymentMethods();
    }, [apiUrl, token]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    // 2. Match code === payment_conditions_code for default selection
    const activeMethod = useMemo(() => {
        if (!payment_conditions_code || !Array.isArray(paymentMethods)) return null;
        const target = String(payment_conditions_code).trim();
        return (
            paymentMethods.find(
                (item) => item?.code != null && String(item.code).trim() === target
            ) || null
        );
    }, [paymentMethods, payment_conditions_code]);

    // 3. Search Filter
    const filteredList = useMemo(() => {
        if (!Array.isArray(paymentMethods)) return [];
        if (!searchQuery) return paymentMethods;
        const q = searchQuery.toLowerCase().trim();
        return paymentMethods.filter((item) =>
            item?.name ? String(item.name).toLowerCase().includes(q) : false
        );
    }, [paymentMethods, searchQuery]);

    const handleSelect = (method) => {
        if (onChange && method) {
            onChange({
                target: {
                    name: "payment_conditions_code",
                    value: String(method.code ?? ""),
                },
            });
        }
        setIsOpen(false);
        setSearchQuery("");
    };

    const paymentError = errors.payment_conditions_code || errors.payment_method;

    return (
        <div className="w-full bg-[#FBFBFB] border-[2px] border-[#8B24332B] rounded-xl shadow-xs relative mb-6">
            {/* Header */}
            <div className="px-5 py-4 border-b border-[#8B2433]/20 bg-[#FAF3F4] rounded-t-[10px] flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <RiBankCardLine className="w-5 h-5 text-[#8B2433] shrink-0" />
                    <div>
                        <h3 className="text-lg font-bold poppins-medium text-[#303030]">
                            Condizione di Pagamento
                        </h3>
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="p-5 sm:p-6 space-y-4">
                {/* Searchable Dropdown Field */}
                <div
                    ref={dropdownRef}
                    className={`flex flex-col gap-1.5 max-w-xl ${
                        isOpen ? "relative z-50" : "relative z-20"
                    }`}
                >
                    <label className="text-xs font-semibold poppins-medium text-[#4A4A4A]">
                        Condizione di Pagamento <span className="text-red-500">*</span>
                    </label>

                    {/* Skeleton Loader during API load */}
                    {isLoading ? (
                        <div className="h-[42px] w-full rounded-3xl border border-[#F2C3CA] bg-gradient-to-r from-[#FCF8F9] via-white to-[#FCF8F9] px-4 flex items-center justify-between cursor-not-allowed select-none shadow-2xs animate-pulse">
                            <div className="flex items-center gap-2.5 min-w-0 pr-3">
                                <span className="w-2 h-2 rounded-full bg-[#8B2433]/25 shrink-0" />
                                <div className="h-3 w-48 sm:w-64 bg-gradient-to-r from-[#8B2433]/15 via-[#8B2433]/8 to-[#8B2433]/15 rounded-full" />
                            </div>
                            <RiArrowDownSLine
                                className="text-[#8B2433]/30 shrink-0"
                                size={20}
                            />
                        </div>
                    ) : (
                        <div className="relative group w-full">
                            {/* Trigger button */}
                            <button
                                type="button"
                                onClick={() => setIsOpen((prev) => !prev)}
                                className={`${
                                    isOpen
                                        ? "border-b-0 rounded-t-[21px] rounded-b-none border-[#8B2433] shadow-xs ring-2 ring-[#8B2433]/15"
                                        : "rounded-3xl border-gray-300 hover:border-[#8B2433]/50"
                                } poppins-regular border inline-flex justify-between items-center w-full px-4 h-[42px] outline-none text-xs bg-white relative z-50 focus:outline-none transition-all cursor-pointer ${
                                    paymentError ? "border-red-500 bg-red-50/20" : ""
                                }`}
                            >
                                {/* Left: Dot & Name */}
                                <div className="flex items-center gap-2.5 min-w-0 flex-1 pr-3">
                                    <span
                                        className={`w-2 h-2 rounded-full shrink-0 ${
                                            activeMethod ? "bg-[#8B2433]" : "bg-gray-300"
                                        }`}
                                    />
                                    <span
                                        className={`text-xs truncate max-w-[220px] sm:max-w-[420px] md:max-w-[460px] ${
                                            activeMethod
                                                ? "font-semibold text-[#303030]"
                                                : "text-gray-400 poppins-regular"
                                        }`}
                                    >
                                        {activeMethod?.name || "Seleziona condizione..."}
                                    </span>
                                </div>

                                {/* Right: Arrow */}
                                <div className="flex items-center gap-2.5 shrink-0 ml-auto">
                                    <RiArrowDownSLine
                                        className={`transform transition-transform duration-300 shrink-0 ${
                                            isOpen ? "rotate-180" : ""
                                        }`}
                                        size={20}
                                        color="#8B2433"
                                    />
                                </div>
                            </button>

                            {/* Searchable Dropdown Panel */}
                            {isOpen && (
                                <div className="bg-white border border-[#8B2433] border-t-0 absolute top-[41px] left-0 right-0 z-50 p-3 pt-2 rounded-b-2xl shadow-[0_16px_36px_rgba(139,36,51,0.12)] space-y-2.5 w-full">
                                    {/* Search input with clear button */}
                                    <div className="flex items-center px-3.5 py-2 border border-gray-200 rounded-full bg-[#FAF5F6] gap-2.5 focus-within:border-[#8B2433] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#8B2433]/15 transition-all">
                                        <img
                                            src={searchIcon}
                                            className="w-4 h-4 opacity-60 shrink-0"
                                            alt="Cerca"
                                        />
                                        <input
                                            type="text"
                                            className="w-full bg-transparent outline-none poppins-regular text-xs text-[#303030] placeholder:text-xs placeholder-gray-400"
                                            placeholder="Cerca condizione..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        {searchQuery && (
                                            <button
                                                type="button"
                                                onClick={() => setSearchQuery("")}
                                                className="text-gray-400 hover:text-gray-600 shrink-0 cursor-pointer"
                                            >
                                                <RiCloseCircleLine className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>

                                    {/* Options List */}
                                    <div className="max-h-[250px] overflow-y-auto btn-all-yscroll space-y-1.5 pr-1">
                                        {filteredList && filteredList.length > 0 ? (
                                            filteredList.map((method, idx) => {
                                                const isSelected =
                                                    activeMethod &&
                                                    String(activeMethod.code).trim() === String(method?.code).trim();

                                                return (
                                                    <div
                                                        key={method?.id ?? method?.code ?? idx}
                                                        onClick={() => handleSelect(method)}
                                                        className={`group flex items-center justify-between gap-3 cursor-pointer py-2.5 px-3 rounded-xl transition-all duration-200 ${
                                                            isSelected
                                                                ? "bg-gradient-to-r from-[#FAF2F4] to-[#FAF6F7] text-[#8B2433] border border-[#8B2433]/30 font-semibold shadow-xs"
                                                                : "hover:bg-[#FAF5F6]/80 text-[#303030] hover:text-[#8B2433] border border-transparent"
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                                            <span
                                                                className={`w-1.5 h-1.5 rounded-full shrink-0 transition-all ${
                                                                    isSelected
                                                                        ? "bg-[#8B2433] scale-110"
                                                                        : "bg-gray-300 group-hover:bg-[#8B2433]/50"
                                                                }`}
                                                            />
                                                            <span className="text-xs poppins-medium truncate max-w-[220px] sm:max-w-[420px] md:max-w-[460px]">
                                                                {method?.name || "N/A"}
                                                            </span>
                                                        </div>

                                                        <div className="shrink-0 flex items-center gap-2.5 ml-auto">
                                                            <div
                                                                className={`w-[18px] h-[18px] rounded-full border flex items-center justify-center transition-all duration-200 ${
                                                                    isSelected
                                                                        ? "border-[#8B2433] bg-[#8B2433] shadow-xs ring-2 ring-[#8B2433]/20"
                                                                        : "border-gray-300 bg-white group-hover:border-[#8B2433]/50"
                                                                }`}
                                                            >
                                                                {isSelected ? (
                                                                    <RiCheckLine className="w-3 h-3 text-white stroke-[0.5]" />
                                                                ) : (
                                                                    <span className="w-1.5 h-1.5 rounded-full bg-[#8B2433]/0 group-hover:bg-[#8B2433]/30 transition-all" />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="py-5 text-center text-xs text-gray-400 poppins-regular">
                                                Nessuna condizione trovata
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {paymentError && (
                        <span className="text-[11px] text-red-500 poppins-regular px-1 mt-0.5">
                            {paymentError}
                        </span>
                    )}
                </div>

                {/* Helper info note */}
                <div className="flex items-start gap-2 p-3.5 bg-[#FAF5F5] border border-[#8B2433]/15 rounded-xl text-xs text-[#505050] poppins-regular">
                    <RiInformationLine className="w-4 h-4 text-[#8B2433] shrink-0 mt-0.5" />
                    <span>
                        Pre-selezionato automaticamente con la condizione abituale del cliente.
                        Utilizza il campo sopra solo se per questa specifica fornitura devi concordare
                        un metodo differente.
                    </span>
                </div>
            </div>
        </div>
    );
};

ProposalOrderPaymentMethod.propTypes = {
    payment_conditions_code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    errors: PropTypes.object,
    validationTrigger: PropTypes.number,
};

export default ProposalOrderPaymentMethod;
